import React, { type FunctionComponent } from 'react'
import { type FormContextValues } from 'react-hook-form'
import moment from 'moment/moment'
import { TimeRangeDropdown } from './TimeRangeDropdown'
import { FormRow } from './ShiftDetailsForm.styles'
import {
  type PartnerPickRecurringShiftsFormData,
  type TimeDropdownOptionType,
} from './recurringShift.types'
import { getSecondsSinceMidnight } from './partnerPickRecurringShiftForm.utils'

// scheduling in 5-minute increment for better granularity
// Ideally we make this configurable on the UI.
// This change would need a new design as right now the pickup shift form is already complex and looks crowded.

const TIME_INCREMENT = 5
interface ShiftDetailsTimeRangeProps {
  register: FormContextValues<PartnerPickRecurringShiftsFormData>['register']
  setValue: FormContextValues<PartnerPickRecurringShiftsFormData>['setValue']
  getValues: FormContextValues<PartnerPickRecurringShiftsFormData>['getValues']
  watch: FormContextValues<PartnerPickRecurringShiftsFormData>['watch']
  fromLabel: string
  toLabel: string
  regularStoreOpen: Date
  regularStoreClose: Date
  displayFormat: string
  isEditMode: boolean
  date: string
  zoneOffset: number
}

export const ShiftDetailsTimeRange: FunctionComponent<ShiftDetailsTimeRangeProps> = ({
  displayFormat,
  regularStoreOpen,
  regularStoreClose,
  fromLabel,
  toLabel,
  setValue,
  getValues,
  register,
  watch,
  isEditMode,
  date,
  zoneOffset,
}) => {
  const generateTimeOptions = (rangeStart, rangeEnd) => {
    const result = []
    result.push({
      label: rangeStart.format(displayFormat),
      value: getSecondsSinceMidnight(rangeStart),
    })
    let pointer = rangeStart.clone().add(TIME_INCREMENT, 'minutes')

    while (pointer.isBetween(rangeStart, rangeEnd, null, '[]')) {
      result.push({
        label: pointer.format(displayFormat),
        value: getSecondsSinceMidnight(pointer),
      })
      pointer = pointer.add(TIME_INCREMENT, 'minutes')
    }
    return result
  }

  const getFromTimeOptions = (): TimeDropdownOptionType[] => {
    let start
    if (isEditMode) {
      start = moment.utc(regularStoreOpen).add(zoneOffset, 'h')
    } else {
      // formData has seconds since midnight, set start to corresponding time
      const secondsSinceMidnight = watch('from')?.value || 0
      start = moment.utc(date).add(secondsSinceMidnight, 's')
    }

    const end = moment.utc(regularStoreClose).add(zoneOffset, 'h')
    return generateTimeOptions(start, end)
  }

  const getToTimeOptions = (): TimeDropdownOptionType[] => {
    let start
    if (isEditMode) {
      start = getMinEndTime()
    } else {
      const secondsSinceMidnight = watch('to')?.value || 0
      start = moment.utc(date).add(secondsSinceMidnight, 's')
    }

    const end = moment.utc(regularStoreClose).add(zoneOffset, 'h')
    return generateTimeOptions(start, end)
  }

  const getMinEndTime = () => {
    const startSecondsSinceMidnight = watch('from')?.value || 0
    const MIN_SHIFT_LENGTH_HOURS = 1 // TODO: is it defined anywhere else?
    return moment.utc(date).add(startSecondsSinceMidnight + MIN_SHIFT_LENGTH_HOURS * 3600, 's')
  }

  const fromTimeOptions = getFromTimeOptions()
  const toTimeOptions = getToTimeOptions()

  const getDefaultValues = () => ({
    from: getValues('from')
      ? getValues('from')
      : moment.utc(regularStoreOpen).add(zoneOffset, 'h').format(displayFormat),
    to: getValues('to')
      ? getValues('to')
      : moment.utc(regularStoreClose).add(zoneOffset, 'h').format(displayFormat),
  })

  const defaultValues = getDefaultValues()

  return (
    <FormRow>
      <TimeRangeDropdown
        name="from"
        data-testid="shopper-shift-form-time-range-from-select"
        label={fromLabel}
        defaultValue={defaultValues.from as TimeDropdownOptionType}
        setValue={setValue}
        getValues={getValues}
        register={register}
        options={fromTimeOptions}
        regularStoreOpen={regularStoreOpen}
      />

      <TimeRangeDropdown
        name="to"
        data-testid="shopper-shift-form-time-range-to-select"
        label={toLabel}
        defaultValue={defaultValues.to as TimeDropdownOptionType}
        setValue={setValue}
        register={register}
        getValues={getValues}
        options={toTimeOptions}
        regularStoreOpen={regularStoreOpen}
      />
    </FormRow>
  )
}
